import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  Users,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ChevronLeft,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  Star,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = ({ children, isAuthenticated, setIsAuthenticated }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
    { name: "Articles", path: "/admin/articles", icon: FileText },
    { name: "Messages", path: "/admin/messages", icon: MessageSquare },
    { name: "Leads", path: "/admin/leads", icon: Users },
    { name: "Témoignages", path: "/admin/testimonials", icon: Star },
  ];

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("admin_auth");
    navigate("/admin");
  };

  if (!isAuthenticated) {
    return children;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="admin-sidebar w-64 flex-shrink-0 p-6">
        <div className="flex items-center gap-2 mb-10">
          <img 
            src="/logo-freelanceurs.png" 
            alt="FREELANCEURS.io" 
            className="h-10 w-auto"
          />
          <span className="font-manrope font-bold text-lg text-white">
            Admin
          </span>
        </div>

        <nav className="space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item flex items-center gap-3 ${
                location.pathname === item.path ? "active" : ""
              }`}
              data-testid={`admin-nav-${item.name.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="mt-auto pt-10">
          <Link to="/" className="admin-nav-item flex items-center gap-3 mb-2">
            <ChevronLeft className="h-5 w-5" />
            Retour au site
          </Link>
          <button
            onClick={handleLogout}
            className="admin-nav-item flex items-center gap-3 w-full text-left text-red-400 hover:text-red-300"
            data-testid="admin-logout"
          >
            <LogOut className="h-5 w-5" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {children}
      </main>
    </div>
  );
};

// Login Component
const AdminLogin = ({ setIsAuthenticated }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem("admin_auth", "true");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Mot de passe incorrect");
      } else {
        setError("Erreur de connexion. Veuillez réessayer.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img 
            src="/logo-freelanceurs.png" 
            alt="FREELANCEURS.io" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <CardTitle className="font-manrope text-2xl">Administration</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                placeholder="Entrez le mot de passe"
                data-testid="admin-password"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <Button
              type="submit"
              className="w-full bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
              data-testid="admin-login-btn"
              disabled={isLoading}
            >
              {isLoading ? "Connexion..." : "Connexion"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-manrope font-bold text-3xl text-slate-900 mb-8">Dashboard</h1>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Articles</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.articles?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Messages</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.contacts?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Leads</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.simulation_leads || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Publiés</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.articles?.published || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bienvenue dans l'administration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600">
            Depuis ce panneau, vous pouvez gérer les articles du blog, consulter les messages 
            reçus et suivre les leads générés par le simulateur.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Articles Management
const ArticlesManagement = () => {
  const [articles, setArticles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "",
    image_url: "",
    author: "",
    published: false,
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles?published_only=false`);
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/blog/articles/${editingArticle.id}`, formData);
        toast.success("Article mis à jour !");
      } else {
        await axios.post(`${API}/blog/articles`, formData);
        toast.success("Article créé !");
      }
      setShowModal(false);
      resetForm();
      fetchArticles();
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet article ?")) {
      try {
        await axios.delete(`${API}/blog/articles/${id}`);
        toast.success("Article supprimé !");
        fetchArticles();
      } catch (error) {
        console.error("Error deleting article:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image_url: article.image_url || "",
      author: article.author,
      published: article.published,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingArticle(null);
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      category: "",
      image_url: "",
      author: "",
      published: false,
    });
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-manrope font-bold text-3xl text-slate-900">Articles</h1>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
          data-testid="add-article-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouvel article
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead>Auteur</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.length > 0 ? (
              articles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell className="font-medium">{article.title}</TableCell>
                  <TableCell>{article.category}</TableCell>
                  <TableCell>{article.author}</TableCell>
                  <TableCell>
                    <Badge variant={article.published ? "default" : "secondary"}>
                      {article.published ? "Publié" : "Brouillon"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(article)}
                      data-testid={`edit-article-${article.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(article.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-article-${article.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  Aucun article pour le moment
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Article Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope text-xl">
              {editingArticle ? "Modifier l'article" : "Nouvel article"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title">Titre</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    title: e.target.value,
                    slug: generateSlug(e.target.value),
                  });
                }}
                required
                className="mt-1"
                data-testid="article-title"
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug (URL)</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                required
                className="mt-1"
                data-testid="article-slug"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Catégorie</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="mt-1"
                  placeholder="Ex: Statuts juridiques"
                  data-testid="article-category"
                />
              </div>
              <div>
                <Label htmlFor="author">Auteur</Label>
                <Input
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="article-author"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="image_url">URL de l'image</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="mt-1"
                placeholder="https://..."
                data-testid="article-image"
              />
            </div>
            <div>
              <Label htmlFor="excerpt">Extrait</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                required
                className="mt-1"
                rows={2}
                data-testid="article-excerpt"
              />
            </div>
            <div>
              <Label htmlFor="content">Contenu (Markdown)</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                className="mt-1 font-mono text-sm"
                rows={10}
                placeholder="## Titre&#10;&#10;Votre contenu ici..."
                data-testid="article-content"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="published"
                checked={formData.published}
                onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                data-testid="article-published"
              />
              <Label htmlFor="published">Publier l'article</Label>
            </div>
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
                data-testid="save-article-btn"
              >
                {editingArticle ? "Mettre à jour" : "Créer l'article"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Messages Management
const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`);
      fetchMessages();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div data-testid="admin-messages">
      <h1 className="font-manrope font-bold text-3xl text-slate-900 mb-8">Messages</h1>

      <div className="space-y-4">
        {messages.length > 0 ? (
          messages.map((message) => (
            <Card
              key={message.id}
              className={message.read ? "opacity-60" : ""}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg text-slate-900">{message.name}</h3>
                      {!message.read && (
                        <Badge className="bg-[#52fbb0] text-slate-900">Nouveau</Badge>
                      )}
                      {message.subject?.startsWith("[Freelance Connect") && (
                        <Badge className="bg-[#10b981] text-white" data-testid="admin-message-freelance-badge">Freelance</Badge>
                      )}
                      {message.subject?.startsWith("[B2B") && (
                        <Badge className="bg-slate-900 text-white" data-testid="admin-message-b2b-badge">B2B</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        {message.email}
                      </span>
                      {message.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          {message.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(message.created_at)}
                      </span>
                    </div>
                    <p className="font-medium text-slate-700">Sujet : {message.subject}</p>
                    <p className="text-slate-600 whitespace-pre-line">{message.message}</p>
                  </div>
                  {!message.read && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markAsRead(message.id)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Marquer lu
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun message pour le moment</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Leads Management
const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/simulation-leads`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div data-testid="admin-leads">
      <h1 className="font-manrope font-bold text-3xl text-slate-900 mb-8">Leads Simulation</h1>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Téléphone</TableHead>
              <TableHead>CA Simulé</TableHead>
              <TableHead>Statut Recommandé</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length > 0 ? (
              leads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell className="font-medium">
                    {lead.first_name} {lead.last_name}
                  </TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{lead.phone}</TableCell>
                  <TableCell>
                    {lead.simulation_data?.inputs?.ca?.toLocaleString("fr-FR")} €
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {lead.simulation_data?.recommended?.name || "N/A"}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(lead.created_at)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  Aucun lead pour le moment
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Testimonials Management
const TestimonialsManagement = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    company: "",
    content: "",
    image_url: "",
    rating: 5,
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/testimonials`, formData);
      toast.success("Témoignage ajouté !");
      setShowModal(false);
      setFormData({
        name: "",
        role: "",
        company: "",
        content: "",
        image_url: "",
        rating: 5,
      });
      fetchTestimonials();
    } catch (error) {
      console.error("Error adding testimonial:", error);
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce témoignage ?")) {
      try {
        await axios.delete(`${API}/testimonials/${id}`);
        toast.success("Témoignage supprimé !");
        fetchTestimonials();
      } catch (error) {
        console.error("Error deleting testimonial:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-manrope font-bold text-3xl text-slate-900">Témoignages</h1>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
          data-testid="add-testimonial-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouveau témoignage
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {testimonials.length > 0 ? (
          testimonials.map((testimonial) => (
            <Card key={testimonial.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {testimonial.image_url ? (
                      <img
                        src={testimonial.image_url}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-[#52fbb0]/20 rounded-full flex items-center justify-center">
                        <span className="font-semibold text-[#52fbb0]">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">
                        {testimonial.role}
                        {testimonial.company && ` - ${testimonial.company}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(testimonial.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-slate-600 italic">"{testimonial.content}"</p>
                <div className="flex gap-1 mt-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#52fbb0] text-[#52fbb0]" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="md:col-span-2">
            <CardContent className="p-12 text-center">
              <Star className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun témoignage pour le moment</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Testimonial Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-xl">
              Nouveau témoignage
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Nom</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Rôle</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  className="mt-1"
                  placeholder="Ex: Développeur web"
                />
              </div>
              <div>
                <Label htmlFor="company">Entreprise</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="image_url">URL de la photo</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="mt-1"
                placeholder="https://..."
              />
            </div>
            <div>
              <Label htmlFor="content">Témoignage</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="rating">Note (1-5)</Label>
              <Input
                id="rating"
                type="number"
                min="1"
                max="5"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                className="mt-1 w-20"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
            >
              Ajouter le témoignage
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Page
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("admin_auth") === "true"
  );

  return (
    <AdminLayout isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated}>
      {!isAuthenticated ? (
        <AdminLogin setIsAuthenticated={setIsAuthenticated} />
      ) : (
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="articles" element={<ArticlesManagement />} />
          <Route path="messages" element={<MessagesManagement />} />
          <Route path="leads" element={<LeadsManagement />} />
          <Route path="testimonials" element={<TestimonialsManagement />} />
        </Routes>
      )}
    </AdminLayout>
  );
}
